import React, { useState, useEffect } from 'react';
import { FaFileSignature, FaCheckCircle, FaTimesCircle, FaEnvelopeOpen, FaExclamationTriangle } from 'react-icons/fa';
import ButtonDefault from '../../../../components/ButtonDefault/index.js';
import LoadingCard from './LoadingCard';
import { CardContainer, CardHeader, CardBody } from './CardStyles';
import api from '../../../../services/api.js';
import { toast } from 'react-toastify';

const D4SignCard = ({ idDm013, idDm014, studyStatus }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [d4SignData, setD4SignData] = useState({
    hasFolder: false,
    hasDocument: false,
    hasSigners: false,
    lastSent: null,
    isApproved: false,
    status: null
  });

  useEffect(() => {
    const fetchStudyData = async () => {
      if (!idDm013) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        // Buscar dados do estudo de viabilidade
        const feasibilityResponse = await api.get(`feasibility_study?ID_DM013=${idDm013}`);
        const data = feasibilityResponse.data.data[0];
        
        if (feasibilityResponse.status === 200) {
          const newD4SignData = {
            hasFolder: !!data.ID_PLATAFORAM_ASSINATURA,
            hasDocument: !!data.ID_DOCUMENTO_ASSINATURA,
            hasSigners: !!data.ID_SIGNATARIO_DOCUMENTO,
            lastSent: data.DT_ENVIO_ASSINATURA,
            isApproved: studyStatus == 3, // Usar o status passado como prop
            status: studyStatus
          };
          setD4SignData(newD4SignData);
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error('Erro ao buscar dados do estudo:', error);
      }
    };

    fetchStudyData();
  }, [idDm013, studyStatus]); // Adicionar studyStatus como dependência

  const handleResendDocument = async () => {
    if (!idDm013) {
      toast.error('ID do estudo não encontrado!');
      return;
    }

    if (!d4SignData.isApproved) {
      toast.error('Apenas estudos aprovados podem ser enviados para assinatura!');
      return;
    }

    setIsSending(true);
    
    try {
      const response = await api.post(`resend?ID_DM014=${idDm014}`);
      
      if (response.status === 200) {
        const result = response.data;
        
        toast.success(`Documento reenviado com sucesso para ${result.customer_name}!`);
        
        // Atualizar os dados locais com as informações retornadas
        setD4SignData(prev => ({
          ...prev,
          hasFolder: true,
          hasDocument: true,
          hasSigners: true,
          lastSent: result.results.send_date || new Date().toISOString()
        }));

        // Log das etapas executadas (para debug)
        console.log('Etapas completadas:', result.results.steps_completed);
        console.log('Etapas executadas:', result.results.steps_executed);
        
        // Mostrar informações adicionais sobre o que foi feito
        if (result.results.steps_executed.length > 0) {
          const executedSteps = result.results.steps_executed.map(step => {
            switch (step) {
              case 'folder_created': return 'Pasta criada';
              case 'document_generated': return 'Documento gerado';
              case 'signers_registered': return 'Assinantes registrados';
              case 'document_sent': return 'Documento enviado';
              default: return step;
            }
          }).join(', ');
          
          toast.info(`Ações executadas: ${executedSteps}`, { autoClose: 5000 });
        }
        
      } else {
        toast.error(response.data.message || 'Erro ao reenviar documento!');
      }
    } catch (error) {
      console.error('Erro ao reenviar documento:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro interno ao reenviar documento!');
      }
    } finally {
      setIsSending(false);
    }
  };

  const getStatusDescription = (status) => {
    const statusMap = {
      1: 'Pendente',
      2: 'Em Análise',
      3: 'Aprovado',
      4: 'Rejeitado',
      5: 'Cancelado'
    };
    return statusMap[parseInt(status)] || 'Desconhecido';
  };

  const getStatusIcon = (status) => {
    switch (parseInt(status)) {
      case 3:
        return <FaCheckCircle style={{ color: '#28a745', marginRight: '8px' }} />;
      case 4:
      case 5:
        return <FaTimesCircle style={{ color: '#dc3545', marginRight: '8px' }} />;
      default:
        return <FaExclamationTriangle style={{ color: '#ffc107', marginRight: '8px' }} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca enviado';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    } catch (error) {
      return 'Data inválida';
    }
  };

  if (isLoading) {
    return <LoadingCard title="Carregando dados de assinatura..." />;
  }

  return (
    <CardContainer>
      <CardHeader>
        <h5>
          <FaFileSignature style={{ marginRight: '10px' }} />
          Assinatura Digital - D4Sign
        </h5>
      </CardHeader>
      <CardBody>
        <div style={{ marginBottom: '20px' }}>
          <h6 style={{ marginBottom: '15px', color: '#333' }}>Status do Estudo</h6>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '10px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '5px',
            marginBottom: '15px'
          }}>
            {getStatusIcon(studyStatus)}
            <span style={{ fontWeight: '500' }}>
              {getStatusDescription(studyStatus)}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h6 style={{ marginBottom: '15px', color: '#333' }}>Status da Assinatura</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '8px', 
              backgroundColor: d4SignData.hasFolder ? '#d4edda' : '#f8d7da', 
              borderRadius: '5px',
              fontSize: '14px'
            }}>
              {d4SignData.hasFolder ? (
                <FaCheckCircle style={{ color: '#28a745', marginRight: '8px' }} />
              ) : (
                <FaTimesCircle style={{ color: '#dc3545', marginRight: '8px' }} />
              )}
              Pasta criada
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '8px', 
              backgroundColor: d4SignData.hasDocument ? '#d4edda' : '#f8d7da', 
              borderRadius: '5px',
              fontSize: '14px'
            }}>
              {d4SignData.hasDocument ? (
                <FaCheckCircle style={{ color: '#28a745', marginRight: '8px' }} />
              ) : (
                <FaTimesCircle style={{ color: '#dc3545', marginRight: '8px' }} />
              )}
              Documento gerado
            </div>
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              padding: '8px', 
              backgroundColor: d4SignData.hasSigners ? '#d4edda' : '#f8d7da', 
              borderRadius: '5px',
              fontSize: '14px'
            }}>
              {d4SignData.hasSigners ? (
                <FaCheckCircle style={{ color: '#28a745', marginRight: '8px' }} />
              ) : (
                <FaTimesCircle style={{ color: '#dc3545', marginRight: '8px' }} />
              )}
              Assinantes registrados
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h6 style={{ marginBottom: '10px', color: '#333' }}>Último Envio</h6>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '10px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '5px'
          }}>
            <FaEnvelopeOpen style={{ color: '#6c757d', marginRight: '8px' }} />
            <span>{formatDate(d4SignData.lastSent)}</span>
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          paddingTop: '10px',
          borderTop: '1px solid #dee2e6'
        }}>
          <button
            type="button"
            style={{
              width: '250px',
              fontSize: '20px',
              background: isSending || !d4SignData.isApproved ? '#6c757d' : '#2c3e50',
              border: 'none',
              borderRadius: '9px',
              color: 'rgb(255, 255, 255)',
              padding: '10px',
              fontWeight: '500',
              cursor: isSending || !d4SignData.isApproved ? 'not-allowed' : 'pointer',
              opacity: isSending || !d4SignData.isApproved ? 0.6 : 1
            }}
            disabled={isSending || !d4SignData.isApproved}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isSending && d4SignData.isApproved) {
                handleResendDocument();
              }
            }}
          >
            {isSending ? 'Reenviando...' : 'Reenviar para Assinatura'}
          </button>
        </div>

        {!d4SignData.isApproved && (
          <div style={{ 
            marginTop: '15px', 
            padding: '10px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '5px',
            textAlign: 'center',
            fontSize: '14px',
            color: '#856404'
          }}>
            <FaExclamationTriangle style={{ marginRight: '5px' }} />
            Apenas estudos aprovados podem ser enviados para assinatura
          </div>
        )}
      </CardBody>
    </CardContainer>
  );
};

export default D4SignCard;
